import React from "react";
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  FormLabel,
  Typography,
} from "@mui/material";
import { Circle, Pentagon } from "lucide-react";
import { useTranslate } from "@refinedev/core";

export type ShapeType = "CIRCLE" | "POLYGON";

interface ShapeTypeSelectorProps {
  value: ShapeType;
  onChange: (value: ShapeType) => void;
  disabled?: boolean;
}

/**
 * ShapeTypeSelector - Toggle between CIRCLE and POLYGON shape types
 *
 * CIRCLE: Zone defined by center point + radius
 * POLYGON: Zone defined by custom drawn shape (3+ points)
 */
const ShapeTypeSelector: React.FC<ShapeTypeSelectorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const translate = useTranslate();

  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: ShapeType | null
  ) => {
    // Prevent deselection - always keep one selected
    if (newValue !== null) {
      onChange(newValue);
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <FormLabel sx={{ mb: 1, display: "block" }}>
        {translate("sharedPlaces.fields.shapeType")}
      </FormLabel>
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={handleChange}
        aria-label="shape type"
        disabled={disabled}
        fullWidth
        sx={{
          "& .MuiToggleButton-root": {
            py: 1.5,
          },
        }}
      >
        <ToggleButton
          value="CIRCLE"
          aria-label="circle shape"
          sx={{
            "&.Mui-selected": {
              backgroundColor: "primary.light",
              color: "primary.contrastText",
              "&:hover": {
                backgroundColor: "primary.main",
              },
            },
          }}
        >
          <Circle size={20} style={{ marginRight: 8 }} />
          <Box sx={{ textAlign: "left" }}>
            <Typography variant="body2" fontWeight="bold">
              {translate("sharedPlaces.shapeTypes.circle")}
            </Typography>
            <Typography variant="caption" sx={{ display: "block", opacity: 0.8 }}>
              {translate("sharedPlaces.shapeTypes.circleDescription")}
            </Typography>
          </Box>
        </ToggleButton>
        <ToggleButton
          value="POLYGON"
          aria-label="polygon shape"
          sx={{
            "&.Mui-selected": {
              backgroundColor: "secondary.light",
              color: "secondary.contrastText",
              "&:hover": {
                backgroundColor: "secondary.main",
              },
            },
          }}
        >
          <Pentagon size={20} style={{ marginRight: 8 }} />
          <Box sx={{ textAlign: "left" }}>
            <Typography variant="body2" fontWeight="bold">
              {translate("sharedPlaces.shapeTypes.polygon")}
            </Typography>
            <Typography variant="caption" sx={{ display: "block", opacity: 0.8 }}>
              {translate("sharedPlaces.shapeTypes.polygonDescription")}
            </Typography>
          </Box>
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
};

export default ShapeTypeSelector;
